/**
 * **Hinter dem Erzeugungszähler** (HM-133, ADR 0040) — was die Erzeugungsanlagen
 * dahinter über ihre Geräte gemeldet haben.
 *
 * **Nicht in der Bilanz, und die Karte sagt das.** Steht ein Speicher hinter
 * demselben Zähler wie die Module, misst der Zähler netto: PV minus Ladung plus
 * Entladung. Was die Module wirklich erzeugt haben und was durch den Speicher
 * ging, wissen nur ihre Geräte. Die Bilanz bleibt beim Zähler; diese Karte
 * schlüsselt auf, wie die Erzeugungsanlagen es tun (ADR 0038).
 *
 * **Gerechnet wird in der API**, Summen und Ertrag je kWp kommen fertig. Eine
 * Anlage ohne Gerät macht die Summe unbekannt — dann steht dort ein Satz und
 * keine kleinere Zahl.
 */
import { Badge, Card } from "@hausmeter/ui";
import type { ApiComponents } from "@hausmeter/sdk";
import { DefinitionList } from "./definition-list.js";
import { Section } from "./section.js";
import { assetStateGap, assetStateLine, formatKwh } from "../lib/generation-asset-device.js";
import { useGenerationAssetState } from "../lib/queries.js";
import { navigate, routePath, type Route } from "../lib/router.js";

type Behind = ApiComponents["schemas"]["BehindGeneration"];
type BehindAsset = Behind["assets"][number];

const number = (value: number, digits = 2): string =>
  new Intl.NumberFormat("de-DE", { maximumFractionDigits: digits }).format(value);

export function BehindGenerationCard({
  siteId,
  behind,
  netGenerationWh,
  caption,
}: {
  siteId: string;
  behind: Behind;
  /** Was der Erzeugungszähler im Zeitraum gemessen hat — netto, wenn ein Speicher dahinter steht. */
  netGenerationWh: number | null;
  /** Der Zeitraum in Worten, wie über dem Verlauf. */
  caption: string;
}) {
  const unknown = "nicht bestimmbar";
  const rows: (readonly [string, string])[] = [];
  if (behind.pv !== null) {
    rows.push([
      "PV erzeugt (brutto)",
      behind.pv.energyWh === null
        ? unknown
        : behind.pv.specificYieldKwhPerKwp === null
          ? formatKwh(behind.pv.energyWh)
          : `${formatKwh(behind.pv.energyWh)} · ${number(behind.pv.specificYieldKwhPerKwp)} kWh/kWp`,
    ]);
  }
  if (behind.storage !== null) {
    rows.push([
      "Speicher geladen",
      behind.storage.chargedWh === null ? unknown : formatKwh(behind.storage.chargedWh),
    ]);
    rows.push([
      "Speicher entladen",
      behind.storage.dischargedWh === null ? unknown : formatKwh(behind.storage.dischargedWh),
    ]);
  }
  if (netGenerationWh !== null) {
    rows.push([
      behind.storage === null ? "Am Erzeugungszähler" : "Am Erzeugungszähler (netto)",
      formatKwh(netGenerationWh),
    ]);
  }

  const withoutDevice = behind.assets.filter((asset) => asset.deviceCount === 0);
  return (
    <Section
      title="Hinter dem Erzeugungszähler"
      subtitle={`${caption} · aus den Geräten der Erzeugungsanlagen, nicht in der Bilanz`}
    >
      <Card>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-4)" }}>
          {rows.length > 0 && <DefinitionList rows={rows} />}
          {behind.storage !== null && (
            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
              Der Speicher steht hinter demselben Zähler wie die Module. Der Zähler sieht deshalb nur,
              was am Ende herauskommt — PV minus Ladung plus Entladung, abzüglich der Verluste. Die
              Bilanz rechnet mit diesem Wert; die PV brutto und der Speicher stehen nur hier.
            </p>
          )}
          {withoutDevice.length > 0 && (
            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
              {withoutDevice.map((asset) => asset.label).join(", ")}{" "}
              {withoutDevice.length === 1 ? "hat" : "haben"} kein Gerät — ohne{" "}
              {withoutDevice.length === 1 ? "seine" : "ihre"} Werte ist die Summe nicht bestimmbar.
            </p>
          )}
          {behind.partial && (
            <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-muted)" }}>
              Bei mindestens einem Gerät beginnen die Werte erst in diesem Zeitraum; gezählt wird ab
              seinem ersten Wert.
            </p>
          )}
          <div style={{ display: "flex", flexDirection: "column" }}>
            {behind.assets.map((asset) => (
              <AssetRow key={asset.generationAssetId} siteId={siteId} asset={asset} />
            ))}
          </div>
        </div>
      </Card>
    </Section>
  );
}

/** Eine Anlage: Name als Link zur Detailseite, ihr Zeitraum und was sie jetzt meldet. */
function AssetRow({ siteId, asset }: { siteId: string; asset: BehindAsset }) {
  const state = useGenerationAssetState(asset.deviceCount === 0 ? null : asset.generationAssetId);
  const route: Route = {
    kind: "generation-asset",
    siteId,
    assetId: asset.generationAssetId,
    range: "24h",
  };
  const period =
    asset.kind === "pv"
      ? asset.energyInWh === null
        ? null
        : `erzeugt ${formatKwh(asset.energyInWh)}`
      : asset.energyInWh === null || asset.energyOutWh === null
        ? null
        : `geladen ${formatKwh(asset.energyInWh)} · entladen ${formatKwh(asset.energyOutWh)}`;
  const now =
    state.data === undefined ? null : (assetStateLine(state.data) ?? assetStateGap(state.data));
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "var(--sp-2) var(--sp-4)",
        alignItems: "baseline",
        justifyContent: "space-between",
        padding: "10px 0",
        borderTop: "1px solid var(--border-subtle)",
      }}
    >
      <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: "1 1 200px" }}>
        <a
          href={routePath(route)}
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
            event.preventDefault();
            navigate(route);
          }}
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            color: "var(--text-strong)",
            textDecoration: "none",
            overflowWrap: "anywhere",
          }}
        >
          {asset.label}
        </a>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {asset.kind === "pv" ? "PV-Anlage" : "Speicher"}
          {period === null ? "" : ` · ${period}`}
        </span>
      </span>
      <span
        style={{
          fontSize: 12.5,
          color: "var(--text-body)",
          fontVariantNumeric: "tabular-nums",
          textAlign: "right",
        }}
      >
        {asset.deviceCount === 0 ? <Badge tone="neutral">kein Gerät</Badge> : (now ?? "")}
      </span>
    </div>
  );
}
